import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { GetNextPageParamFunction } from "@tanstack/react-query";

import { useCallback, useMemo } from "react";

import { toast } from "@/lib/use-toast";

import {
  LocationBreakPolicy,
  LocationDetail,
  LocationFilters,
  PublicLocation,
} from "@/types/location";
import { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";
import { calendarQueryKeys } from "@/query/check-in/calendar/calendar-query";
import { eventQueryKeys } from "@/query/check-in/calendar/event-query";

async function listLocations(
  params?: LocationFilters,
): Promise<Page<LocationDetail> | null> {
  return await requestData<Page<LocationDetail>>(
    "admin/locations/list",
    "get",
    {
      searchParams: params,
    },
  );
}

async function getLocation(locationId: string): Promise<LocationDetail | null> {
  return await requestData<LocationDetail>(
    `admin/locations/${locationId}`,
    "get",
  );
}

async function deleteLocation(locationId: string): Promise<boolean | null> {
  return await requestData<boolean>(
    `admin/locations/delete/${locationId}`,
    "delete",
    {},
    false,
  );
}

async function updateOrCreateLocation(
  formData: FormData,
): Promise<LocationDetail | null> {
  return await requestData<LocationDetail>(
    "admin/locations/update-or-create",
    "post",
    {
      body: formData,
    },
    false, // Don't catch errors, let them propagate so we can show error messages
  );
}

async function updateLocationBreakPolicy(
  locationId: string,
  policy: LocationBreakPolicy,
): Promise<LocationBreakPolicy | null> {
  return await requestData<LocationBreakPolicy>(
    `admin/locations/${locationId}/break-policy`,
    "put",
    { json: policy },
    false,
  );
}

async function getPublicLocations(
  tenantName: string,
): Promise<PublicLocation[] | null> {
  const headers = tenantName ? { TENANT: tenantName } : undefined;
  return await requestData<PublicLocation[]>("admin/locations/public", "get", {
    headers,
  });
}

export const locationQueryKeys = {
  all: ["settings", "location"] as const,
  list: (filters?: LocationFilters) =>
    [...locationQueryKeys.all, "list", filters] as const,
  detail: (locationId: string) =>
    [...locationQueryKeys.all, "detail", locationId] as const,
  public: (tenantName: string) =>
    [...locationQueryKeys.all, "public", tenantName] as const,
};

export const locationQueryOptions = {
  list: (filters?: LocationFilters) => ({
    queryKey: locationQueryKeys.list(filters),
    queryFn: () => listLocations(filters),
  }),
  detail: (locationId: string) => ({
    queryKey: locationQueryKeys.detail(locationId),
    queryFn: () => getLocation(locationId),
  }),
  public: (tenantName: string) => ({
    queryKey: locationQueryKeys.public(tenantName),
    queryFn: () => getPublicLocations(tenantName),
  }),
};

export const useCreateOrUpdateLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => updateOrCreateLocation(formData),
    onMutate: async (formData: FormData) => {
      await queryClient.cancelQueries({ queryKey: locationQueryKeys.all });

      // Extract location ID from form data if it exists
      const locationId = formData.get("id")?.toString();
      const name = formData.get("name")?.toString() ?? "";

      // If updating, we need to get the previous data
      if (locationId) {
        const detailKey = locationQueryKeys.detail(locationId);
        const previousDetail =
          queryClient.getQueryData<LocationDetail>(detailKey);

        // Optimistically update the detail
        const optimisticDetail: LocationDetail = {
          ...previousDetail,
          id: locationId,
          name,
          description: formData.get("description")?.toString(),
          addressLineOne: formData.get("addressLineOne")?.toString(),
          addressLineTwo: formData.get("addressLineTwo")?.toString(),
          city: formData.get("city")?.toString(),
          state: formData.get("state")?.toString(),
          zipCode: formData.get("zipCode")?.toString(),
          country: formData.get("country")?.toString(),
          isVirtual: formData.get("isVirtual") === "true",
          timezone: formData.get("timezone")?.toString() ?? "",
          photoUrl: formData.get("photoUrl")?.toString(),
          reviewLink: formData.get("reviewLink")?.toString(),
          latitude: formData.get("latitude")
            ? Number.parseFloat(formData.get("latitude")!.toString())
            : undefined,
          longitude: formData.get("longitude")
            ? Number.parseFloat(formData.get("longitude")!.toString())
            : undefined,
          spakinectLocationId: formData.get("spakinectLocationId")?.toString(),
          taxIdentificationNumber: formData
            .get("taxIdentificationNumber")
            ?.toString(),
          mrnCode: formData.get("mrnCode")?.toString(),
        } as LocationDetail;

        queryClient.setQueryData<LocationDetail>(detailKey, optimisticDetail);

        // Update in list queries
        const listQueries = queryClient.getQueriesData<Page<LocationDetail>>({
          queryKey: locationQueryKeys.list(),
        });

        for (const [queryKey, pageData] of listQueries) {
          if (!pageData?.data) continue;
          const previousItems = pageData.data;
          const nextItems = previousItems.map((loc) =>
            loc.id === locationId ? optimisticDetail : loc,
          );
          queryClient.setQueryData<Page<LocationDetail>>(queryKey, {
            ...pageData,
            data: nextItems,
          });
        }

        return { previousDetail, locationId, isUpdate: true };
      } else {
        // Creating new location - optimistic add to list
        const optimisticLocation: LocationDetail = {
          id: "", // Will be set by server
          name,
          description: formData.get("description")?.toString(),
          addressLineOne: formData.get("addressLineOne")?.toString(),
          addressLineTwo: formData.get("addressLineTwo")?.toString(),
          city: formData.get("city")?.toString(),
          state: formData.get("state")?.toString(),
          zipCode: formData.get("zipCode")?.toString(),
          country: formData.get("country")?.toString(),
          isVirtual: formData.get("isVirtual") === "true",
          timezone: formData.get("timezone")?.toString() ?? "",
          photoUrl: formData.get("photoUrl")?.toString(),
          reviewLink: formData.get("reviewLink")?.toString(),
          latitude: formData.get("latitude")
            ? Number.parseFloat(formData.get("latitude")!.toString())
            : undefined,
          longitude: formData.get("longitude")
            ? Number.parseFloat(formData.get("longitude")!.toString())
            : undefined,
          spakinectLocationId: formData.get("spakinectLocationId")?.toString(),
          taxIdentificationNumber: formData
            .get("taxIdentificationNumber")
            ?.toString(),
          mrnCode: formData.get("mrnCode")?.toString(),
        } as LocationDetail;

        const listQueries = queryClient.getQueriesData<Page<LocationDetail>>({
          queryKey: locationQueryKeys.list(),
        });

        const previousPages: Array<{
          queryKey: ReturnType<typeof locationQueryKeys.list>;
          previousData: Page<LocationDetail> | undefined;
        }> = [];

        for (const [queryKey, pageData] of listQueries) {
          const previous = pageData;
          const next: Page<LocationDetail> = {
            ...pageData,
            data: [...(pageData?.data ?? []), optimisticLocation],
            total: (pageData?.total ?? 0) + 1,
          };
          queryClient.setQueryData<Page<LocationDetail>>(queryKey, next);
          previousPages.push({
            queryKey: queryKey as ReturnType<typeof locationQueryKeys.list>,
            previousData: previous,
          });
        }

        return { previousPages, isUpdate: false };
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create or update location",
        variant: "error",
      });
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.locationId) {
        queryClient.invalidateQueries({
          queryKey: locationQueryKeys.detail(context.locationId),
        });
      }
      queryClient.invalidateQueries({ queryKey: locationQueryKeys.list() });
      queryClient.invalidateQueries({
        queryKey: calendarQueryKeys.locations(),
      });
    },
  });
};

export const useDeleteLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) => deleteLocation(locationId),
    onMutate: async (locationId: string) => {
      await queryClient.cancelQueries({ queryKey: locationQueryKeys.all });

      // Get previous detail
      const detailKey = locationQueryKeys.detail(locationId);
      const previousDetail =
        queryClient.getQueryData<LocationDetail>(detailKey);

      // Remove from detail cache
      queryClient.removeQueries({ queryKey: detailKey });

      // Remove from list queries
      const listQueries = queryClient.getQueriesData<Page<LocationDetail>>({
        queryKey: locationQueryKeys.list(),
      });

      const previousPages: Array<{
        queryKey: ReturnType<typeof locationQueryKeys.list>;
        previousData: Page<LocationDetail> | undefined;
      }> = [];

      for (const [queryKey, pageData] of listQueries) {
        const previous = pageData;
        if (!pageData?.data) continue;

        const nextItems = pageData.data.filter((loc) => loc.id !== locationId);
        const next: Page<LocationDetail> = {
          ...pageData,
          data: nextItems,
          total: Math.max(0, (pageData.total ?? 0) - 1),
        };
        queryClient.setQueryData<Page<LocationDetail>>(queryKey, next);
        previousPages.push({
          queryKey: queryKey as ReturnType<typeof locationQueryKeys.list>,
          previousData: previous,
        });
      }

      return { previousDetail, previousPages, locationId };
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to archive location",
        variant: "error",
      });
    },
    onSettled: (_data, _error, _variables, context) => {
      if (!context || !context.locationId) return;
      queryClient.invalidateQueries({
        queryKey: locationQueryKeys.detail(context.locationId),
      });
      queryClient.invalidateQueries({ queryKey: locationQueryKeys.list() });
      queryClient.invalidateQueries({
        queryKey: calendarQueryKeys.locations(),
      });
    },
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Query Hooks
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_PER_PAGE = 20;

interface UseLocationsOptions {
  filters?: LocationFilters;
  enabled?: boolean;
}

/**
 * Hook to fetch locations with infinite loading support.
 * Accepts any LocationFilters for flexible querying.
 */
export function useLocations({
  filters,
  enabled = true,
}: UseLocationsOptions = {}) {
  const queryClient = useQueryClient();

  const getQueryFn = (payload: {
    pageParam?: number;
    filters?: LocationFilters;
  }) => {
    return async () => {
      const queryFilters: LocationFilters = {
        ...(payload?.filters ?? {}),
        page: payload?.pageParam ?? 1,
        perPage: payload?.filters?.perPage ?? DEFAULT_PER_PAGE,
      };

      const response = await listLocations(queryFilters);
      return response ?? { data: [], total: 0 };
    };
  };

  const getNextPageParam: GetNextPageParamFunction<
    number,
    Page<LocationDetail>
  > = (lastPage, allPages) => {
    const loadedCount = allPages.reduce(
      (sum, page) => sum + page.data.length,
      0,
    );
    if (loadedCount < lastPage.total) {
      return allPages.length + 1;
    }
    return undefined;
  };

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: locationQueryKeys.list(filters),
      queryFn: ({ pageParam }) => getQueryFn({ filters, pageParam })(),
      getNextPageParam,
      initialPageParam: 1,
      enabled,
      staleTime: 1000 * 60 * 60, // 60 minutes
    });

  // Flatten all pages into a single array of locations
  const locations = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data?.pages]);

  const totalLocations = data?.pages?.[0]?.total ?? 0;

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // This is hacky, we should probaly create a generic useLazyQuery / useLazyInfiniteQuery hook(s)
  const lazyFetch = (filters: LocationFilters) => {
    const queryKey = locationQueryKeys.list(filters);
    return queryClient.ensureInfiniteQueryData({
      queryKey,
      queryFn: ({ pageParam }) => getQueryFn({ filters, pageParam })(),
      getNextPageParam,
      initialPageParam: 1,
      staleTime: 1000 * 60 * 60, // 60 minutes
    });
  };

  return {
    locations,
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage: handleFetchNextPage,
    lazyFetch,
    totalLocations,
  };
}

export const useUpdateLocationBreakPolicyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationId,
      policy,
    }: {
      locationId: string;
      policy: LocationBreakPolicy;
    }) => updateLocationBreakPolicy(locationId, policy),
    onSuccess: (saved, { locationId }) => {
      if (!saved) return;
      const apply = (location: LocationDetail): LocationDetail =>
        location.id === locationId ? { ...location, ...saved } : location;
      queryClient.setQueryData<LocationDetail>(
        locationQueryKeys.detail(locationId),
        (previous) => (previous ? apply(previous) : previous),
      );
      queryClient.setQueriesData<Page<LocationDetail>>(
        { queryKey: [...locationQueryKeys.all, "list"] },
        (page) => (page ? { ...page, data: page.data.map(apply) } : page),
      );
      queryClient.setQueriesData<LocationDetail[]>(
        { queryKey: calendarQueryKeys.locationsRoot },
        (locations) =>
          Array.isArray(locations) ? locations.map(apply) : locations,
      );
      // Cached plans were computed against the old rules.
      queryClient.removeQueries({
        queryKey: [...eventQueryKeys.shifts(), "break-plan", locationId],
      });
      toast({ title: "Break rules saved", variant: "success" });
    },
    onError: (error) => {
      toast({
        title: "Couldn't save break rules",
        description: error instanceof Error ? error.message : undefined,
        variant: "error",
      });
    },
  });
};

export interface LocationSendingAddress {
  locationId: string;
  emailFromLocalPart: string | null;
}

async function updateLocationSendingAddresses(
  addresses: LocationSendingAddress[],
): Promise<null> {
  return await requestData<null>(
    "admin/locations/sending-addresses",
    "put",
    { json: { addresses } },
    false,
  );
}

/** Sets or clears the inbox send address of the listed locations; others are untouched. */
export const useUpdateLocationSendingAddressesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLocationSendingAddresses,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: locationQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: calendarQueryKeys.locations(),
      });
    },
  });
};
